import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { alumniAssociationAPI, eventsAPI, uploadAPI } from '../lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  GraduationCap, Users, Activity, CalendarDays, FileText, Search,
  Send, Plus, Download, MapPin, Mail, BarChart3, TrendingUp, Megaphone,
  Heart, DollarSign, Target, Clock, Loader2, Eye, Camera, X,
  Briefcase, Building2, ArrowUpDown, UserPlus, Video, ExternalLink, Globe
} from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import usePageTitle from '../hooks/usePageTitle';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const AlumniAssociationPanel = () => {
  usePageTitle('Alumni Association');
  const { user } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);

  // Dashboard state
  const [dashboard, setDashboard] = useState(null);

  // Members state
  const [members, setMembers] = useState([]);
  const [membersTotal, setMembersTotal] = useState(0);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberYear, setMemberYear] = useState('');
  const [memberMajor, setMemberMajor] = useState('');
  const [memberLocation, setMemberLocation] = useState('');
  const [memberIndustry, setMemberIndustry] = useState('');
  const [memberCompany, setMemberCompany] = useState('');
  const [memberGender, setMemberGender] = useState('');
  const [memberMentorFilter, setMemberMentorFilter] = useState('');
  const [memberSortBy, setMemberSortBy] = useState('created_at');
  const [memberSortOrder, setMemberSortOrder] = useState('desc');
  const [workforceStats, setWorkforceStats] = useState(null);

  // Meetings state
  const [meetings, setMeetings] = useState([]);
  const [showMeetingDialog, setShowMeetingDialog] = useState(false);
  const [newMeeting, setNewMeeting] = useState({ title: '', description: '', date: '', time: '', platform: 'zoom', meeting_url: '', invited_ids: [] });
  const [creatingMeeting, setCreatingMeeting] = useState(false);

  // Recommend talent state
  const [showRecommendDialog, setShowRecommendDialog] = useState(false);
  const [recommendData, setRecommendData] = useState({ talent_id: '', recipient_id: '', message: '' });
  const [recommendingTalent, setRecommendingTalent] = useState(false);

  // Communications state
  const [announceTitle, setAnnounceTitle] = useState('');
  const [announceMessage, setAnnounceMessage] = useState('');
  const [sendingAnnouncement, setSendingAnnouncement] = useState(false);
  const [sentAnnouncements, setSentAnnouncements] = useState([]);

  // Events state
  const [events, setEvents] = useState([]);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '', description: '', date: '', time: '', location: '', type: 'networking'
  });
  const [creatingEvent, setCreatingEvent] = useState(false);

  // Analytics state
  const [analytics, setAnalytics] = useState(null);

  // Fundraising state
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [newCampaign, setNewCampaign] = useState({ title: '', description: '', goal_amount: '', deadline: '', cover_image: '' });
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDonors, setCampaignDonors] = useState([]);
  const [loadingDonors, setLoadingDonors] = useState(false);

  // Auth guard
  useEffect(() => {
    const hasAccess = user?.roles?.includes('alumni_association') || user?.roles?.includes('admin') || user?.role === 'admin';
    if (!hasAccess) {
      navigate('/dashboard');
      return;
    }
    fetchDashboard();
  }, [user, navigate]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const res = await alumniAssociationAPI.getDashboard();
      setDashboard(res.data);
    } catch (e) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMeetings = async () => {
    try {
      const res = await alumniAssociationAPI.getMeetings();
      setMeetings(Array.isArray(res.data) ? res.data : []);
    } catch { setMeetings([]); }
  };

  const handleCreateMeeting = async () => {
    if (!newMeeting.title || !newMeeting.date || !newMeeting.meeting_url) {
      toast.error('Title, date, and meeting URL are required'); return;
    }
    setCreatingMeeting(true);
    try {
      await alumniAssociationAPI.createMeeting(newMeeting);
      toast.success('Meeting created and invitations sent!');
      setShowMeetingDialog(false);
      setNewMeeting({ title: '', description: '', date: '', time: '', platform: 'zoom', meeting_url: '', invited_ids: [] });
      fetchMeetings();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create meeting'); }
    finally { setCreatingMeeting(false); }
  };

  const handleRecommendTalent = async () => {
    if (!recommendData.talent_id || !recommendData.recipient_id) {
      toast.error('Please select both the talent and the recipient'); return;
    }
    setRecommendingTalent(true);
    try {
      await alumniAssociationAPI.recommendTalent(recommendData);
      toast.success('Recommendation sent!');
      setShowRecommendDialog(false);
      setRecommendData({ talent_id: '', recipient_id: '', message: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send recommendation'); }
    finally { setRecommendingTalent(false); }
  };

  const fetchMembers = useCallback(async () => {
    try {
      const params = { limit: 50, sort_by: memberSortBy, sort_order: memberSortOrder };
      if (memberSearch) params.search = memberSearch;
      if (memberYear) params.graduation_year = memberYear;
      if (memberMajor) params.major = memberMajor;
      if (memberLocation) params.location = memberLocation;
      if (memberIndustry) params.industry = memberIndustry;
      if (memberCompany) params.company = memberCompany;
      if (memberGender) params.gender = memberGender;
      if (memberMentorFilter) params.mentor_status = memberMentorFilter;
      const res = await alumniAssociationAPI.getMembers(params);
      setMembers(res.data.members || []);
      setMembersTotal(res.data.total || 0);
      if (res.data.workforce_stats) setWorkforceStats(res.data.workforce_stats);
    } catch (e) {
      toast.error('Failed to load members');
    }
  }, [memberSearch, memberYear, memberMajor, memberLocation]);

  const fetchEvents = async () => {
    try {
      const res = await alumniAssociationAPI.getEvents({ limit: 50 });
      setEvents(res.data.events || []);
    } catch (e) {
      toast.error('Failed to load events');
    }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await alumniAssociationAPI.getAnalytics();
      setAnalytics(res.data);
    } catch (e) {
      toast.error('Failed to load analytics');
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'members') fetchMembers();
    if (tab === 'events') fetchEvents();
    if (tab === 'analytics') fetchAnalytics();
    if (tab === 'fundraising') fetchCampaigns();
    if (tab === 'mentorship') fetchMembers();
    if (tab === 'meetings') fetchMeetings();
  };

  const fetchCampaigns = async () => {
    setLoadingCampaigns(true);
    try {
      const res = await alumniAssociationAPI.getCampaigns();
      setCampaigns(Array.isArray(res.data) ? res.data : []);
    } catch { setCampaigns([]); }
    finally { setLoadingCampaigns(false); }
  };

  const handleCreateCampaign = async () => {
    if (!newCampaign.title || !newCampaign.goal_amount) {
      toast.error('Title and goal amount are required');
      return;
    }
    setCreatingCampaign(true);
    try {
      await alumniAssociationAPI.createCampaign({
        ...newCampaign,
        goal_amount: parseFloat(newCampaign.goal_amount),
      });
      toast.success('Campaign created!');
      setShowCreateCampaign(false);
      setNewCampaign({ title: '', description: '', goal_amount: '', deadline: '', cover_image: '' });
      fetchCampaigns();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create campaign');
    } finally { setCreatingCampaign(false); }
  };

  const viewCampaignDonors = async (campaign) => {
    setSelectedCampaign(campaign);
    setLoadingDonors(true);
    try {
      const res = await alumniAssociationAPI.getCampaignDonors(campaign.campaign_id);
      setCampaignDonors(Array.isArray(res.data) ? res.data : []);
    } catch { setCampaignDonors([]); }
    finally { setLoadingDonors(false); }
  };

  const toggleCampaignActive = async (campaign) => {
    try {
      await alumniAssociationAPI.updateCampaign(campaign.campaign_id, { is_active: !campaign.is_active });
      toast.success(campaign.is_active ? 'Campaign paused' : 'Campaign reactivated');
      fetchCampaigns();
    } catch { toast.error('Failed to update campaign'); }
  };

  const handleMemberSearch = (e) => {
    e.preventDefault();
    fetchMembers();
  };

  const handleSendAnnouncement = async () => {
    if (!announceTitle.trim() || !announceMessage.trim()) {
      toast.error('Please fill in both title and message');
      return;
    }
    try {
      setSendingAnnouncement(true);
      await alumniAssociationAPI.announce({
        title: announceTitle,
        message: announceMessage
      });
      toast.success('Announcement sent successfully');
      setSentAnnouncements(prev => [
        { title: announceTitle, message: announceMessage, sent_at: new Date().toISOString() },
        ...prev
      ]);
      setAnnounceTitle('');
      setAnnounceMessage('');
    } catch (e) {
      toast.error('Failed to send announcement');
    } finally {
      setSendingAnnouncement(false);
    }
  };

  const handleCreateEvent = async () => {
    if (!newEvent.title.trim() || !newEvent.date) {
      toast.error('Title and date are required');
      return;
    }
    try {
      setCreatingEvent(true);
      const eventData = {
        title: newEvent.title,
        description: newEvent.description,
        location: newEvent.location,
        type: newEvent.type,
        date: newEvent.date + (newEvent.time ? `T${newEvent.time}` : 'T00:00'),
      };
      await eventsAPI.create(eventData);
      toast.success('Event created successfully');
      setShowEventDialog(false);
      setNewEvent({ title: '', description: '', date: '', time: '', location: '', type: 'networking' });
      fetchEvents();
    } catch (e) {
      toast.error('Failed to create event');
    } finally {
      setCreatingEvent(false);
    }
  };

  const exportMembersCSV = () => {
    if (members.length === 0) {
      toast.error('No members to export');
      return;
    }
    const headers = ['Name', 'Email', 'Graduation Year', 'Major', 'Location'];
    const rows = members.map(m => [
      m.full_name || m.name || '',
      m.email || '',
      m.graduation_year || '',
      m.major || '',
      m.location || ''
    ]);
    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `alumni_members_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported successfully');
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
        </div>
      </Layout>
    );
  }

  const stats = dashboard?.stats || {};
  const recentMembers = dashboard?.recent_members || [];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl">
            <GraduationCap className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              Alumni Association
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Manage your alumni network and communications
            </p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="flex flex-wrap h-auto bg-slate-100 dark:bg-slate-800">
            <TabsTrigger value="dashboard" className="gap-1.5">
              <BarChart3 className="h-4 w-4" /> Dashboard
            </TabsTrigger>
            <TabsTrigger value="members" className="gap-1.5">
              <Users className="h-4 w-4" /> Members
            </TabsTrigger>
            <TabsTrigger value="communications" className="gap-1.5">
              <Megaphone className="h-4 w-4" /> Communications
            </TabsTrigger>
            <TabsTrigger value="events" className="gap-1.5">
              <CalendarDays className="h-4 w-4" /> Events
            </TabsTrigger>
            <TabsTrigger value="analytics" className="gap-1.5">
              <TrendingUp className="h-4 w-4" /> Analytics
            </TabsTrigger>
            <TabsTrigger value="mentorship" className="gap-1.5">
              <UserPlus className="h-4 w-4" />Mentorship
            </TabsTrigger>
            <TabsTrigger value="meetings" className="gap-1.5">
              <Video className="h-4 w-4" />Meetings
            </TabsTrigger>
            <TabsTrigger value="fundraising" className="gap-1.5">
              <Heart className="h-4 w-4" /> Fundraising
            </TabsTrigger>
          </TabsList>

          {/* ===== DASHBOARD TAB ===== */}
          <TabsContent value="dashboard" className="space-y-6">
            {/* Stats cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="rounded-xl p-5 bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-emerald-100 text-sm font-medium">Total Members</p>
                    <p className="text-3xl font-bold mt-1">{stats.member_count ?? 0}</p>
                  </div>
                  <Users className="h-10 w-10 text-emerald-200 opacity-80" />
                </div>
              </div>
              <div className="rounded-xl p-5 bg-gradient-to-br from-blue-500 to-blue-700 text-white shadow-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm font-medium">Active (30 days)</p>
                    <p className="text-3xl font-bold mt-1">{stats.active_30d ?? 0}</p>
                  </div>
                  <Activity className="h-10 w-10 text-blue-200 opacity-80" />
                </div>
              </div>
              <div className="rounded-xl p-5 bg-gradient-to-br from-violet-500 to-violet-700 text-white shadow-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-violet-100 text-sm font-medium">Events</p>
                    <p className="text-3xl font-bold mt-1">{stats.events_count ?? 0}</p>
                  </div>
                  <CalendarDays className="h-10 w-10 text-violet-200 opacity-80" />
                </div>
              </div>
              <div className="rounded-xl p-5 bg-gradient-to-br from-amber-500 to-amber-700 text-white shadow-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-amber-100 text-sm font-medium">Posts</p>
                    <p className="text-3xl font-bold mt-1">{stats.posts_count ?? 0}</p>
                  </div>
                  <FileText className="h-10 w-10 text-amber-200 opacity-80" />
                </div>
              </div>
            </div>

            {/* Recent members */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Members</CardTitle>
              </CardHeader>
              <CardContent>
                {recentMembers.length === 0 ? (
                  <p className="text-slate-500 dark:text-slate-400 text-sm">No recent members to show.</p>
                ) : (
                  <div className="space-y-3">
                    {recentMembers.map((member, idx) => (
                      <div key={member.user_id || idx} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={member.profile_picture || member.avatar} alt={member.full_name || member.name} />
                          <AvatarFallback className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300 text-sm font-medium">
                            {getInitials(member.full_name || member.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 dark:text-white truncate">
                            {member.full_name || member.name}
                          </p>
                          <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                            {member.major && `${member.major} `}
                            {member.graduation_year && `'${String(member.graduation_year).slice(-2)}`}
                          </p>
                        </div>
                        {member.joined_at && (
                          <span className="text-xs text-slate-400 dark:text-slate-500 shrink-0">
                            {formatDistanceToNow(new Date(member.joined_at), { addSuffix: true })}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== MEMBERS TAB ===== */}
          <TabsContent value="members" className="space-y-4">
            {/* Search & filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <form onSubmit={handleMemberSearch} className="flex-1 flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search members by name or email..."
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Button type="submit" variant="outline">Search</Button>
              </form>
              <Button onClick={exportMembersCSV} variant="outline" className="gap-1.5 shrink-0">
                <Download className="h-4 w-4" /> Export CSV
              </Button>
            </div>

            <div className="flex flex-wrap gap-2">
              <Select value={memberYear} onValueChange={(v) => { setMemberYear(v === 'all' ? '' : v); }}>
                <SelectTrigger className="w-[140px] h-9 text-xs"><SelectValue placeholder="Class Year" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  {Array.from({ length: 30 }, (_, i) => new Date().getFullYear() - i).map(y => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={memberMajor} onValueChange={(v) => { setMemberMajor(v === 'all' ? '' : v); }}>
                <SelectTrigger className="w-[150px] h-9 text-xs"><SelectValue placeholder="Major" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Majors</SelectItem>
                  {['Computer Science', 'Business', 'Engineering', 'Biology', 'Psychology', 'Communications', 'Education', 'Nursing', 'Political Science', 'Mathematics', 'Economics', 'English', 'Sociology'].map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={memberLocation} onValueChange={(v) => { setMemberLocation(v === 'all' ? '' : v); }}>
                <SelectTrigger className="w-[150px] h-9 text-xs"><SelectValue placeholder="City" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  {['Atlanta, GA', 'Washington, DC', 'New York, NY', 'Houston, TX', 'Charlotte, NC', 'Chicago, IL', 'Los Angeles, CA', 'Dallas, TX', 'Baltimore, MD', 'Detroit, MI'].map(l => (
                    <SelectItem key={l} value={l}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={memberIndustry} onValueChange={(v) => { setMemberIndustry(v === 'all' ? '' : v); }}>
                <SelectTrigger className="w-[150px] h-9 text-xs"><SelectValue placeholder="Industry" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Industries</SelectItem>
                  {['Technology', 'Healthcare', 'Finance', 'Education', 'Law', 'Engineering', 'Marketing', 'Real Estate', 'Government', 'Entertainment', 'Non-Profit', 'Consulting'].map(i => (
                    <SelectItem key={i} value={i}>{i}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={memberGender} onValueChange={(v) => { setMemberGender(v === 'all' ? '' : v); }}>
                <SelectTrigger className="w-[120px] h-9 text-xs"><SelectValue placeholder="Gender" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="non_binary">Non-Binary</SelectItem>
                </SelectContent>
              </Select>
              <Select value={memberMentorFilter} onValueChange={(v) => { setMemberMentorFilter(v === 'all' ? '' : v); }}>
                <SelectTrigger className="w-[150px] h-9 text-xs"><SelectValue placeholder="Mentorship" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Members</SelectItem>
                  <SelectItem value="mentor">Willing to Mentor</SelectItem>
                  <SelectItem value="mentee">Seeking Mentor</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="h-9 text-xs gap-1" onClick={fetchMembers}>
                <Search className="w-3.5 h-3.5" />Apply
              </Button>
              <Button variant="outline" size="sm" className="h-9 text-xs gap-1" onClick={() => setShowRecommendDialog(true)}>
                <UserPlus className="w-3.5 h-3.5" />Recommend
              </Button>
            </div>

            {/* Workforce stats cards */}
            {workforceStats && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="rounded-lg p-3 bg-blue-50 border border-blue-100">
                  <p className="text-xs font-medium text-blue-600">Top Industries</p>
                  <div className="mt-1.5 space-y-0.5">
                    {(workforceStats.top_industries || []).slice(0, 3).map((ind, i) => (
                      <p key={i} className="text-xs text-slate-600 flex justify-between"><span>{ind._id}</span><span className="font-medium">{ind.count}</span></p>
                    ))}
                  </div>
                </div>
                <div className="rounded-lg p-3 bg-emerald-50 border border-emerald-100">
                  <p className="text-xs font-medium text-emerald-600">Top Employers</p>
                  <div className="mt-1.5 space-y-0.5">
                    {(workforceStats.top_companies || []).slice(0, 3).map((comp, i) => (
                      <p key={i} className="text-xs text-slate-600 flex justify-between"><span>{comp._id}</span><span className="font-medium">{comp.count}</span></p>
                    ))}
                  </div>
                </div>
                <div className="rounded-lg p-3 bg-purple-50 border border-purple-100">
                  <p className="text-xs font-medium text-purple-600">Mentors Available</p>
                  <p className="text-2xl font-bold text-purple-700 mt-1">{workforceStats.mentor_count || 0}</p>
                </div>
                <div className="rounded-lg p-3 bg-amber-50 border border-amber-100">
                  <p className="text-xs font-medium text-amber-600">Seeking Mentorship</p>
                  <p className="text-2xl font-bold text-amber-700 mt-1">{workforceStats.mentee_count || 0}</p>
                </div>
              </div>
            )}

            {/* Members count */}
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {membersTotal} member{membersTotal !== 1 ? 's' : ''} found
            </p>

            {/* Members table */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700">
                      <th className="text-left font-medium text-slate-600 dark:text-slate-300 px-4 py-3">Member</th>
                      <th className="text-left font-medium text-slate-600 dark:text-slate-300 px-4 py-3">Title / Company</th>
                      <th className="text-left font-medium text-slate-600 dark:text-slate-300 px-4 py-3">Industry</th>
                      <th className="text-left font-medium text-slate-600 dark:text-slate-300 px-4 py-3">Class</th>
                      <th className="text-left font-medium text-slate-600 dark:text-slate-300 px-4 py-3">Location</th>
                      <th className="text-left font-medium text-slate-600 dark:text-slate-300 px-4 py-3">Contact</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {members.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-slate-400 dark:text-slate-500">
                          No members found. Try adjusting your search or filters.
                        </td>
                      </tr>
                    ) : (
                      members.map((m, idx) => (
                        <tr key={m.user_id || idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-8 w-8">
                                <AvatarImage src={m.profile_picture || m.avatar} alt={m.full_name || m.name} />
                                <AvatarFallback className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300 text-xs font-medium">
                                  {getInitials(m.full_name || m.name)}
                                </AvatarFallback>
                              </Avatar>
                              <span className="font-medium text-slate-900 dark:text-white">
                                {m.full_name || m.name || 'Unknown'}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-slate-600 dark:text-slate-300 text-xs">
                              {m.title && <p className="font-medium">{m.title}</p>}
                              {m.company && <p className="text-slate-400">{m.company}</p>}
                              {!m.title && !m.company && '-'}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300 text-xs">{m.industry || '-'}</td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300 text-xs">
                            {m.graduation_year ? `'${String(m.graduation_year).slice(-2)}` : '-'}
                            {m.major && <span className="text-slate-400 block">{m.major}</span>}
                          </td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300 text-xs">
                            {(m.current_location || m.location) ? (
                              <span className="flex items-center gap-1"><MapPin className="h-3 w-3 text-slate-400" />{m.current_location || m.location}</span>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              {m.email && <a href={`mailto:${m.email}`} className="p-1 rounded hover:bg-slate-100" title={m.email}><Mail className="w-3.5 h-3.5 text-slate-400" /></a>}
                              {m.phone && <a href={`tel:${m.phone}`} className="p-1 rounded hover:bg-slate-100" title={m.phone}><Globe className="w-3.5 h-3.5 text-slate-400" /></a>}
                              {m.willing_to_mentor && <Badge className="text-[9px] bg-purple-100 text-purple-700 border-0 px-1">Mentor</Badge>}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* ===== COMMUNICATIONS TAB ===== */}
          <TabsContent value="communications" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Megaphone className="h-5 w-5 text-emerald-600" />
                  New Announcement
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">
                    Title
                  </label>
                  <Input
                    placeholder="Announcement title..."
                    value={announceTitle}
                    onChange={(e) => setAnnounceTitle(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">
                    Message
                  </label>
                  <Textarea
                    placeholder="Write your announcement message..."
                    value={announceMessage}
                    onChange={(e) => setAnnounceMessage(e.target.value)}
                    rows={5}
                  />
                </div>
                <Button
                  onClick={handleSendAnnouncement}
                  disabled={sendingAnnouncement || !announceTitle.trim() || !announceMessage.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
                >
                  <Send className="h-4 w-4" />
                  {sendingAnnouncement ? 'Sending...' : 'Send Announcement'}
                </Button>
              </CardContent>
            </Card>

            {/* Sent announcements */}
            {sentAnnouncements.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Sent Announcements</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {sentAnnouncements.map((a, idx) => (
                      <div key={idx} className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-slate-900 dark:text-white">{a.title}</h4>
                            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1 whitespace-pre-wrap">{a.message}</p>
                          </div>
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800 shrink-0">
                            Sent
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-400 mt-2">
                          {formatDistanceToNow(new Date(a.sent_at), { addSuffix: true })}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ===== EVENTS TAB ===== */}
          <TabsContent value="events" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Institution Events</h2>
              <Button
                onClick={() => setShowEventDialog(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
              >
                <Plus className="h-4 w-4" /> Create Event
              </Button>
            </div>

            {events.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <CalendarDays className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-500 dark:text-slate-400">No events yet. Create your first event to get started.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {events.map((event, idx) => (
                  <Card key={event._id || event.id || idx}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-slate-900 dark:text-white">{event.title}</h3>
                            <Badge variant="outline" className="text-xs capitalize">
                              {event.type || 'general'}
                            </Badge>
                          </div>
                          {event.description && (
                            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1.5 line-clamp-2">{event.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-3 text-sm text-slate-500 dark:text-slate-400 flex-wrap">
                            {event.date && (
                              <span className="flex items-center gap-1">
                                <CalendarDays className="h-3.5 w-3.5" />
                                {format(new Date(event.date), 'MMM d, yyyy h:mm a')}
                              </span>
                            )}
                            {event.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3.5 w-3.5" />
                                {event.location}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                            {event.rsvp_count ?? event.attendees?.length ?? 0}
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">RSVPs</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Create Event Dialog */}
            <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>Create Event</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">Title</label>
                    <Input
                      placeholder="Event title..."
                      value={newEvent.title}
                      onChange={(e) => setNewEvent(prev => ({ ...prev, title: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">Description</label>
                    <Textarea
                      placeholder="Describe the event..."
                      value={newEvent.description}
                      onChange={(e) => setNewEvent(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">Date</label>
                      <Input
                        type="date"
                        value={newEvent.date}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, date: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">Time</label>
                      <Input
                        type="time"
                        value={newEvent.time}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, time: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">Location</label>
                    <Input
                      placeholder="Event location..."
                      value={newEvent.location}
                      onChange={(e) => setNewEvent(prev => ({ ...prev, location: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">Type</label>
                    <Select
                      value={newEvent.type}
                      onValueChange={(v) => setNewEvent(prev => ({ ...prev, type: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="networking">Networking</SelectItem>
                        <SelectItem value="workshop">Workshop</SelectItem>
                        <SelectItem value="social">Social</SelectItem>
                        <SelectItem value="career">Career Fair</SelectItem>
                        <SelectItem value="fundraiser">Fundraiser</SelectItem>
                        <SelectItem value="homecoming">Homecoming</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowEventDialog(false)}>Cancel</Button>
                  <Button
                    onClick={handleCreateEvent}
                    disabled={creatingEvent || !newEvent.title.trim() || !newEvent.date}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {creatingEvent ? 'Creating...' : 'Create Event'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* ===== ANALYTICS TAB ===== */}
          <TabsContent value="analytics" className="space-y-6">
            {/* Monthly bar chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Monthly Activity</CardTitle>
              </CardHeader>
              <CardContent>
                {analytics?.monthly_data && analytics.monthly_data.length > 0 ? (
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={analytics.monthly_data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#94a3b8' }} />
                      <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1e293b',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#f1f5f9'
                        }}
                      />
                      <Bar dataKey="new_members" name="New Members" fill="#10b981" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="posts" name="Posts" fill="#6366f1" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-slate-400 dark:text-slate-500">
                    <BarChart3 className="h-12 w-12 mb-3" />
                    <p>No analytics data available yet.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Top contributors */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top Contributors</CardTitle>
              </CardHeader>
              <CardContent>
                {analytics?.top_contributors && analytics.top_contributors.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.top_contributors.map((contributor, idx) => (
                      <div key={contributor.user_id || idx} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                        <span className="text-sm font-bold text-slate-400 dark:text-slate-500 w-6 text-center">
                          {idx + 1}
                        </span>
                        <Avatar className="h-9 w-9">
                          <AvatarImage src={contributor.profile_picture || contributor.avatar} alt={contributor.full_name || contributor.name} />
                          <AvatarFallback className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300 text-xs font-medium">
                            {getInitials(contributor.full_name || contributor.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 dark:text-white truncate">
                            {contributor.full_name || contributor.name}
                          </p>
                        </div>
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800">
                          {contributor.post_count ?? contributor.posts ?? 0} posts
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 dark:text-slate-400 text-sm text-center py-8">
                    No contributor data available yet.
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== MENTORSHIP TAB ===== */}
          <TabsContent value="mentorship" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading font-bold text-lg text-dark-slate">Mentorship Directory</h3>
                <p className="text-sm text-slate-gray">View alumni who are willing to mentor or seeking mentorship</p>
              </div>
            </div>

            {/* Mentor/Mentee quick stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-xl p-5 bg-gradient-to-br from-purple-500 to-purple-700 text-white shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                onClick={() => { setMemberMentorFilter('mentor'); setActiveTab('members'); setTimeout(fetchMembers, 100); }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100 text-sm font-medium">Available Mentors</p>
                    <p className="text-3xl font-bold mt-1">{workforceStats?.mentor_count || 0}</p>
                    <p className="text-purple-200 text-xs mt-1">Click to view all mentors →</p>
                  </div>
                  <UserPlus className="h-10 w-10 text-purple-200 opacity-80" />
                </div>
              </div>
              <div className="rounded-xl p-5 bg-gradient-to-br from-amber-500 to-amber-700 text-white shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                onClick={() => { setMemberMentorFilter('mentee'); setActiveTab('members'); setTimeout(fetchMembers, 100); }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-amber-100 text-sm font-medium">Seeking Mentorship</p>
                    <p className="text-3xl font-bold mt-1">{workforceStats?.mentee_count || 0}</p>
                    <p className="text-amber-200 text-xs mt-1">Click to view all mentees →</p>
                  </div>
                  <GraduationCap className="h-10 w-10 text-amber-200 opacity-80" />
                </div>
              </div>
            </div>

            {/* Filter mentors by location/industry */}
            <Card>
              <CardHeader><CardTitle className="text-base">Find Mentors by Location or Industry</CardTitle></CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 mb-4">
                  {['Atlanta, GA', 'Washington, DC', 'New York, NY', 'Houston, TX', 'Chicago, IL', 'Los Angeles, CA'].map(city => (
                    <Button key={city} variant="outline" size="sm" className="text-xs rounded-full"
                      onClick={() => { setMemberLocation(city); setMemberMentorFilter('mentor'); setActiveTab('members'); setTimeout(fetchMembers, 100); }}>
                      <MapPin className="w-3 h-3 mr-1" />{city}
                    </Button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  {['Technology', 'Healthcare', 'Finance', 'Law', 'Education', 'Engineering', 'Marketing', 'Real Estate'].map(ind => (
                    <Button key={ind} variant="outline" size="sm" className="text-xs rounded-full"
                      onClick={() => { setMemberIndustry(ind); setMemberMentorFilter('mentor'); setActiveTab('members'); setTimeout(fetchMembers, 100); }}>
                      <Briefcase className="w-3 h-3 mr-1" />{ind}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Recommend Talent</CardTitle></CardHeader>
              <CardContent>
                <p className="text-sm text-slate-gray mb-3">Connect alumni by recommending one member's talent to another.</p>
                <Button onClick={() => setShowRecommendDialog(true)} className="bg-dark-slate text-white rounded-full">
                  <UserPlus className="w-4 h-4 mr-2" />Send a Recommendation
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== MEETINGS TAB ===== */}
          <TabsContent value="meetings" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading font-bold text-lg text-dark-slate">Association Meetings</h3>
                <p className="text-sm text-slate-gray">Plan meetings, invite members, and host via Zoom, Teams, or Google Meet</p>
              </div>
              <Button onClick={() => setShowMeetingDialog(true)} className="bg-dark-slate text-white rounded-full">
                <Plus className="w-4 h-4 mr-2" />Schedule Meeting
              </Button>
            </div>

            {meetings.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <Video className="w-12 h-12 text-slate-gray mx-auto mb-4" />
                  <h3 className="font-bold text-lg text-dark-slate mb-2">No meetings scheduled</h3>
                  <p className="text-slate-gray text-sm">Schedule your first meeting to connect with alumni</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {meetings.map(mtg => (
                  <Card key={mtg.meeting_id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold text-dark-slate">{mtg.title}</h4>
                          {mtg.description && <p className="text-sm text-slate-gray mt-0.5">{mtg.description}</p>}
                          <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-gray">
                            <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{mtg.date}</span>
                            {mtg.time && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{mtg.time}</span>}
                            <Badge variant="secondary" className="text-[10px]">
                              {mtg.platform === 'zoom' ? 'Zoom' : mtg.platform === 'teams' ? 'Microsoft Teams' : mtg.platform === 'google_meet' ? 'Google Meet' : mtg.platform}
                            </Badge>
                            <span className="text-slate-400">{(mtg.invited_ids || []).length} invited</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {mtg.meeting_url && (
                            <a href={mtg.meeting_url} target="_blank" rel="noopener noreferrer">
                              <Button size="sm" className="bg-dark-slate text-white rounded-full text-xs">
                                <ExternalLink className="w-3.5 h-3.5 mr-1" />Join Meeting
                              </Button>
                            </a>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ===== FUNDRAISING TAB ===== */}
          <TabsContent value="fundraising" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading font-bold text-xl text-dark-slate">Fundraising Campaigns</h2>
                <p className="text-sm text-slate-gray">Create and manage fundraising campaigns for your institution</p>
              </div>
              <Button onClick={() => setShowCreateCampaign(true)} className="bg-dark-slate text-white rounded-full">
                <Plus className="w-4 h-4 mr-2" />New Campaign
              </Button>
            </div>

            {loadingCampaigns ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-gray" /></div>
            ) : campaigns.length === 0 ? (
              <Card className="border-0 shadow-md">
                <CardContent className="text-center py-12">
                  <Heart className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-heading font-bold text-lg text-dark-slate mb-2">No campaigns yet</h3>
                  <p className="text-slate-gray text-sm mb-4">Create your first fundraising campaign to start collecting donations</p>
                  <Button onClick={() => setShowCreateCampaign(true)} className="bg-dark-slate text-white rounded-full">
                    <Plus className="w-4 h-4 mr-2" />Create Campaign
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {campaigns.map(camp => {
                  const progress = camp.goal_amount > 0 ? Math.min(100, (camp.raised_amount / camp.goal_amount) * 100) : 0;
                  const daysLeft = camp.deadline ? Math.max(0, Math.ceil((new Date(camp.deadline) - new Date()) / (1000 * 60 * 60 * 24))) : null;
                  return (
                    <Card key={camp.campaign_id} className={`border-0 shadow-md overflow-hidden ${!camp.is_active ? 'opacity-60' : ''}`}>
                      {camp.cover_image && (
                        <img src={camp.cover_image} alt="" className="w-full h-36 object-cover" />
                      )}
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-heading font-bold text-dark-slate">{camp.title}</h3>
                          {!camp.is_active && <Badge className="bg-slate-200 text-slate-600 text-[10px]">Paused</Badge>}
                        </div>
                        {camp.description && <p className="text-sm text-slate-gray line-clamp-2 mb-3">{camp.description}</p>}

                        {/* Progress bar */}
                        <div className="mb-2">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="font-semibold text-dark-slate">${camp.raised_amount?.toLocaleString()}</span>
                            <span className="text-slate-gray">of ${camp.goal_amount?.toLocaleString()}</span>
                          </div>
                          <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-xs text-slate-gray mb-3">
                          <span>{camp.donor_count || 0} donor{camp.donor_count !== 1 ? 's' : ''}</span>
                          {daysLeft !== null && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{daysLeft} days left</span>}
                        </div>

                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="rounded-full text-xs flex-1" onClick={() => viewCampaignDonors(camp)}>
                            <Eye className="w-3.5 h-3.5 mr-1" />Donors
                          </Button>
                          <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => toggleCampaignActive(camp)}>
                            {camp.is_active ? 'Pause' : 'Reactivate'}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Fundraising Platform Integrations */}
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="text-lg text-dark-slate flex items-center gap-2">
                  <ExternalLink className="w-5 h-5" />Connect a Fundraising Platform
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-gray mb-4">Link your existing fundraising platform so alumni can donate directly. Campaigns will link out to your platform.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">

                  {/* GoFundMe */}
                  <div className="rounded-xl border border-slate-200 p-4 hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <Heart className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-dark-slate text-sm">GoFundMe</h4>
                        <p className="text-[10px] text-slate-gray">Personal & community fundraising</p>
                      </div>
                    </div>
                    <Input placeholder="https://gofundme.com/your-campaign" className="h-8 text-xs rounded-lg mb-2" id="int-gofundme" />
                    <Button size="sm" variant="outline" className="w-full rounded-full text-xs h-7" onClick={() => {
                      const url = document.getElementById('int-gofundme')?.value;
                      if (url) { toast.success('GoFundMe linked!'); } else { toast.error('Enter your GoFundMe URL'); }
                    }}>Connect</Button>
                  </div>

                  {/* GiveCampus */}
                  <div className="rounded-xl border border-slate-200 p-4 hover:border-blue-300 hover:bg-blue-50/30 transition-colors">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <GraduationCap className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-dark-slate text-sm">GiveCampus</h4>
                        <p className="text-[10px] text-slate-gray">Higher ed fundraising & engagement</p>
                      </div>
                    </div>
                    <Input placeholder="https://givecampus.com/your-school" className="h-8 text-xs rounded-lg mb-2" id="int-givecampus" />
                    <Button size="sm" variant="outline" className="w-full rounded-full text-xs h-7" onClick={() => {
                      const url = document.getElementById('int-givecampus')?.value;
                      if (url) { toast.success('GiveCampus linked!'); } else { toast.error('Enter your GiveCampus URL'); }
                    }}>Connect</Button>
                  </div>

                  {/* Classy */}
                  <div className="rounded-xl border border-slate-200 p-4 hover:border-purple-300 hover:bg-purple-50/30 transition-colors">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                        <Target className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-dark-slate text-sm">Classy</h4>
                        <p className="text-[10px] text-slate-gray">Non-profit fundraising platform</p>
                      </div>
                    </div>
                    <Input placeholder="https://classy.org/your-campaign" className="h-8 text-xs rounded-lg mb-2" id="int-classy" />
                    <Button size="sm" variant="outline" className="w-full rounded-full text-xs h-7" onClick={() => {
                      const url = document.getElementById('int-classy')?.value;
                      if (url) { toast.success('Classy linked!'); } else { toast.error('Enter your Classy URL'); }
                    }}>Connect</Button>
                  </div>

                  {/* Blackbaud / Raiser's Edge */}
                  <div className="rounded-xl border border-slate-200 p-4 hover:border-amber-300 hover:bg-amber-50/30 transition-colors">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                        <BarChart3 className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-dark-slate text-sm">Blackbaud</h4>
                        <p className="text-[10px] text-slate-gray">Raiser's Edge & donor management</p>
                      </div>
                    </div>
                    <Input placeholder="https://app.blackbaud.com/your-org" className="h-8 text-xs rounded-lg mb-2" id="int-blackbaud" />
                    <Button size="sm" variant="outline" className="w-full rounded-full text-xs h-7" onClick={() => {
                      const url = document.getElementById('int-blackbaud')?.value;
                      if (url) { toast.success('Blackbaud linked!'); } else { toast.error('Enter your Blackbaud URL'); }
                    }}>Connect</Button>
                  </div>

                  {/* Givebutter */}
                  <div className="rounded-xl border border-slate-200 p-4 hover:border-pink-300 hover:bg-pink-50/30 transition-colors">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-pink-100 flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-pink-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-dark-slate text-sm">Givebutter</h4>
                        <p className="text-[10px] text-slate-gray">Free fundraising for nonprofits</p>
                      </div>
                    </div>
                    <Input placeholder="https://givebutter.com/your-campaign" className="h-8 text-xs rounded-lg mb-2" id="int-givebutter" />
                    <Button size="sm" variant="outline" className="w-full rounded-full text-xs h-7" onClick={() => {
                      const url = document.getElementById('int-givebutter')?.value;
                      if (url) { toast.success('Givebutter linked!'); } else { toast.error('Enter your Givebutter URL'); }
                    }}>Connect</Button>
                  </div>

                  {/* Custom / Other */}
                  <div className="rounded-xl border border-slate-200 p-4 hover:border-slate-400 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                        <Globe className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-dark-slate text-sm">Other Platform</h4>
                        <p className="text-[10px] text-slate-gray">PayPal, Venmo, Cash App, custom</p>
                      </div>
                    </div>
                    <Input placeholder="https://your-donation-page.com" className="h-8 text-xs rounded-lg mb-2" id="int-other" />
                    <Button size="sm" variant="outline" className="w-full rounded-full text-xs h-7" onClick={() => {
                      const url = document.getElementById('int-other')?.value;
                      if (url) { toast.success('Platform linked!'); } else { toast.error('Enter your donation URL'); }
                    }}>Connect</Button>
                  </div>

                </div>
              </CardContent>
            </Card>

            {/* Create Campaign Dialog */}
            <Dialog open={showCreateCampaign} onOpenChange={setShowCreateCampaign}>
              <DialogContent className="max-w-md rounded-2xl">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">Create Fundraising Campaign</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1">Campaign Title *</label>
                    <Input value={newCampaign.title} onChange={(e) => setNewCampaign(p => ({ ...p, title: e.target.value }))}
                      placeholder="e.g. Class of 2010 Scholarship Fund" className="rounded-xl" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1">Description</label>
                    <Textarea value={newCampaign.description} onChange={(e) => setNewCampaign(p => ({ ...p, description: e.target.value }))}
                      placeholder="What is this campaign for?" rows={3} className="rounded-xl" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium text-slate-700 block mb-1">Goal Amount *</label>
                      <Input type="number" value={newCampaign.goal_amount} onChange={(e) => setNewCampaign(p => ({ ...p, goal_amount: e.target.value }))}
                        placeholder="5000" className="rounded-xl" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 block mb-1">Deadline</label>
                      <Input type="date" value={newCampaign.deadline} onChange={(e) => setNewCampaign(p => ({ ...p, deadline: e.target.value }))}
                        className="rounded-xl" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1">Cover Image URL</label>
                    <Input value={newCampaign.cover_image} onChange={(e) => setNewCampaign(p => ({ ...p, cover_image: e.target.value }))}
                      placeholder="https://..." className="rounded-xl" />
                  </div>
                  <Button onClick={handleCreateCampaign} disabled={creatingCampaign} className="w-full h-11 bg-dark-slate text-white rounded-full">
                    {creatingCampaign ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</> : 'Create Campaign'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Donors Dialog */}
            <Dialog open={!!selectedCampaign} onOpenChange={() => setSelectedCampaign(null)}>
              <DialogContent className="max-w-md rounded-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">Donors — {selectedCampaign?.title}</DialogTitle>
                </DialogHeader>
                <div className="space-y-2 mt-4">
                  {loadingDonors ? (
                    <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-gray" /></div>
                  ) : campaignDonors.length === 0 ? (
                    <p className="text-slate-gray text-sm text-center py-8">No donations yet</p>
                  ) : (
                    <>
                      <div className="bg-emerald-50 rounded-xl p-3 mb-3 text-center">
                        <p className="text-2xl font-bold text-emerald-700">${selectedCampaign?.raised_amount?.toLocaleString()}</p>
                        <p className="text-xs text-emerald-600">{campaignDonors.length} donation{campaignDonors.length !== 1 ? 's' : ''}</p>
                      </div>
                      {campaignDonors.map(don => (
                        <div key={don.donation_id} className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
                          <div>
                            <p className="font-medium text-dark-slate text-sm">{don.donor_name || 'Anonymous'}</p>
                            {don.created_at && <p className="text-[10px] text-slate-gray">{formatDistanceToNow(new Date(don.created_at), { addSuffix: true })}</p>}
                          </div>
                          <span className="font-semibold text-emerald-700">${don.amount?.toLocaleString()}</span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>
        </Tabs>

        {/* ===== MEETING DIALOG ===== */}
        <Dialog open={showMeetingDialog} onOpenChange={setShowMeetingDialog}>
          <DialogContent className="max-w-md rounded-2xl">
            <DialogHeader><DialogTitle>Schedule a Meeting</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div><Input placeholder="Meeting title" value={newMeeting.title} onChange={(e) => setNewMeeting(p => ({ ...p, title: e.target.value }))} /></div>
              <div><Textarea placeholder="Description (optional)" value={newMeeting.description} onChange={(e) => setNewMeeting(p => ({ ...p, description: e.target.value }))} rows={2} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Input type="date" value={newMeeting.date} onChange={(e) => setNewMeeting(p => ({ ...p, date: e.target.value }))} /></div>
                <div><Input type="time" value={newMeeting.time} onChange={(e) => setNewMeeting(p => ({ ...p, time: e.target.value }))} /></div>
              </div>
              <Select value={newMeeting.platform} onValueChange={(v) => setNewMeeting(p => ({ ...p, platform: v }))}>
                <SelectTrigger><SelectValue placeholder="Platform" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="zoom">Zoom</SelectItem>
                  <SelectItem value="teams">Microsoft Teams</SelectItem>
                  <SelectItem value="google_meet">Google Meet</SelectItem>
                  <SelectItem value="webex">Webex</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              <div><Input placeholder="Meeting URL (paste your Zoom/Teams link)" value={newMeeting.meeting_url} onChange={(e) => setNewMeeting(p => ({ ...p, meeting_url: e.target.value }))} /></div>
              <p className="text-xs text-slate-gray">Members of your institution will be notified about this meeting.</p>
              <DialogFooter>
                <Button onClick={handleCreateMeeting} disabled={creatingMeeting} className="bg-dark-slate text-white rounded-full w-full">
                  {creatingMeeting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</> : <><Video className="w-4 h-4 mr-2" />Schedule Meeting</>}
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* ===== RECOMMEND DIALOG ===== */}
        <Dialog open={showRecommendDialog} onOpenChange={setShowRecommendDialog}>
          <DialogContent className="max-w-md rounded-2xl">
            <DialogHeader><DialogTitle>Recommend Alumni Talent</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-gray">Recommend a talented alumni member to another member of the community.</p>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium">Alumni to Recommend</label>
                <Select value={recommendData.talent_id} onValueChange={(v) => setRecommendData(p => ({ ...p, talent_id: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select alumni..." /></SelectTrigger>
                  <SelectContent>
                    {members.map(m => <SelectItem key={m.user_id} value={m.user_id}>{m.full_name || m.name}{m.title ? ` — ${m.title}` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Recommend To</label>
                <Select value={recommendData.recipient_id} onValueChange={(v) => setRecommendData(p => ({ ...p, recipient_id: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select recipient..." /></SelectTrigger>
                  <SelectContent>
                    {members.filter(m => m.user_id !== recommendData.talent_id).map(m => <SelectItem key={m.user_id} value={m.user_id}>{m.full_name || m.name}{m.company ? ` — ${m.company}` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Message (optional)</label>
                <Textarea value={recommendData.message} onChange={(e) => setRecommendData(p => ({ ...p, message: e.target.value }))} placeholder="Why you're recommending this person..." rows={2} className="mt-1" />
              </div>
              <DialogFooter>
                <Button onClick={handleRecommendTalent} disabled={recommendingTalent} className="bg-dark-slate text-white rounded-full w-full">
                  {recommendingTalent ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</> : <><Send className="w-4 h-4 mr-2" />Send Recommendation</>}
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AlumniAssociationPanel;
